// All diagnostic test categories offered by Lifeline Diagnostics
// Each test carries a short, patient-friendly explanation of its clinical use.

export const testCategories = [
  {
    id: "microbiology",
    number: "01",
    title: "Microbiology",
    tagline: "Isolating pathogens, guiding therapy.",
    tests: [
      { name: "Microscopic Examination", desc: "Direct microscopy of clinical samples to detect bacteria, fungi and parasites — the fastest first look at an infection." },
      { name: "Gram Staining", desc: "Classifies bacteria as Gram-positive or Gram-negative to steer initial antibiotic choice within minutes." },
      { name: "Albert Staining", desc: "Highlights the metachromatic granules of Corynebacterium diphtheriae — key in suspected diphtheria." },
      { name: "Giemsa Staining", desc: "Used to visualize blood parasites (malaria, leishmania) and certain bacteria in tissue smears." },
      { name: "Ziehl–Neelsen Staining", desc: "The acid-fast stain used to detect Mycobacterium tuberculosis and other mycobacteria in sputum and body fluids." },
      { name: "KOH Preparation", desc: "Dissolves keratin to reveal fungal hyphae and yeast in skin, nail and hair samples." },
      { name: "Bacterial Culture and Sensitivity", desc: "Grows the causative bacteria from a sample and tests which antibiotics will actually kill it." },
      { name: "Fungal Culture", desc: "Isolates and identifies pathogenic fungi from clinical specimens for accurate antifungal therapy." },
      { name: "Tuberculosis Culture", desc: "Gold-standard culture for Mycobacterium tuberculosis to confirm active TB and guide treatment." },
      { name: "Antibiotic Sensitivity Testing", desc: "Maps the exact antibiotics a patient's bacteria are susceptible to — the backbone of stewardship." },
      { name: "Operation Theatre Swab Culture", desc: "Surveillance culture from OT surfaces to verify sterilization and prevent surgical-site infections." },
      { name: "ICU Room Culture", desc: "Environmental monitoring of ICU rooms to detect nosocomial pathogens before they reach patients." },
      { name: "Air Settle Plate Testing", desc: "Quantifies airborne microbial load in critical zones — required for NABH-compliant facilities." },
      { name: "Anaerobic Culture", desc: "Cultivates bacteria that grow only without oxygen — vital in abscesses, deep wounds and bloodstream infections." },
      { name: "Disinfectant Efficiency Testing", desc: "Verifies that the hospital's disinfectants actually kill the microbes they claim to." },
      { name: "Bacterial Analysis of Water", desc: "Tests drinking, dialysis and RO water for coliforms and total bacterial counts against IS/WHO limits." },
    ],
  },
  {
    id: "serology",
    number: "02",
    title: "Serology",
    tagline: "Antibodies, antigens, immune signatures.",
    tests: [
      { name: "Widal Test", desc: "Detects antibodies against Salmonella Typhi/Paratyphi — used to support diagnosis of enteric (typhoid) fever." },
      { name: "Pregnancy Test", desc: "Detects β-hCG in urine to confirm pregnancy at the earliest reliable stage." },
      { name: "Blood Group & Rh. Factor", desc: "Identifies ABO group and Rh status — essential before transfusion, pregnancy and surgery." },
      { name: "VDRL / RPR / TPHA", desc: "Screens and confirms syphilis infection through non-treponemal and treponemal antibody testing." },
      { name: "Toxoplasma", desc: "Detects Toxoplasma gondii antibodies — critical during pregnancy to protect the fetus." },
      { name: "A.S.O. Titre", desc: "Measures antibodies to Streptococcus — helps diagnose rheumatic fever and post-streptococcal disease." },
      { name: "Australia Antigen (HBsAg)", desc: "Screens for Hepatitis B surface antigen — the primary marker of active HBV infection." },
      { name: "C-Reactive Protein (CRP)", desc: "A sensitive marker of acute inflammation and infection anywhere in the body." },
      { name: "ADA Test for Tuberculosis", desc: "Adenosine deaminase in body fluids — a rapid aid in diagnosing tubercular effusions and meningitis." },
      { name: "Malaria Antibody Test", desc: "Detects antibodies to Plasmodium species when active parasites are not seen on smear." },
      { name: "Brucella Antibody Test", desc: "Diagnoses brucellosis — a zoonotic infection often missed as prolonged fever." },
      { name: "Prostate Specific Antigen (PSA)", desc: "Screens for prostate cancer and monitors response after treatment." },
      { name: "Dengue Virus Antibody Test", desc: "Detects IgM/IgG antibodies to dengue for confirmation of recent or past infection." },
      { name: "HCV Antibody Test", desc: "Screens for Hepatitis C virus exposure — the first step in the HCV diagnostic pathway." },
      { name: "ANA Test", desc: "Antinuclear antibodies — the entry screen for autoimmune conditions like SLE." },
      { name: "Chikungunya Antibody Test", desc: "Confirms chikungunya viral infection during outbreaks of febrile arthralgia." },
      { name: "TORCH Test", desc: "Panel for Toxoplasma, Rubella, CMV and Herpes — screens infections that can harm the fetus." },
      { name: "HbeAg", desc: "Marker of active Hepatitis B viral replication and higher infectivity." },
      { name: "Troponin Test", desc: "Highly specific marker of cardiac muscle injury — used in suspected heart attack." },
      { name: "Leptospira Antibody Test", desc: "Diagnoses leptospirosis, an important cause of monsoon-season fever with jaundice." },
      { name: "COVID IgG & IgM Antibody Test", desc: "Detects past or recent exposure to SARS-CoV-2." },
    ],
  },
  {
    id: "hematology",
    number: "03",
    title: "Hematology",
    tagline: "The story blood cells tell.",
    tests: [
      { name: "Complete Blood Count (CBC)", desc: "A full snapshot of red cells, white cells and platelets — the most-ordered test in medicine." },
      { name: "Hemoglobin", desc: "Measures oxygen-carrying protein in blood — the standard test for anemia." },
      { name: "Differential Count", desc: "Percentages of each white cell type — points toward infection, allergy or blood disorders." },
      { name: "LE Cell Preparation", desc: "Detects lupus erythematosus cells to support a diagnosis of SLE." },
      { name: "Peripheral Smear for Malaria Parasite", desc: "Direct microscopic identification of malarial parasites in blood — the diagnostic gold standard." },
      { name: "Platelet Count", desc: "Assesses platelets — crucial in dengue, bleeding disorders and pre-surgical workups." },
      { name: "Bleeding & Clotting Time", desc: "Bedside tests of primary hemostasis — used before minor surgeries and dental work." },
      { name: "Reticulocyte Count", desc: "Measures young red cells — shows how well the bone marrow is responding to anemia." },
      { name: "Prothrombin Time (PT/INR)", desc: "Monitors the extrinsic clotting pathway — essential for patients on warfarin." },
      { name: "Sickling Test", desc: "Screens for sickle cell hemoglobin, common in tribal and rural belts of Maharashtra." },
      { name: "D-Dimer", desc: "Detects clot breakdown products — helps rule out DVT, pulmonary embolism and DIC." },
      { name: "Coomb's Test — Direct / Indirect", desc: "Detects antibodies attached to red cells — key in hemolytic anemia and pregnancy." },
      { name: "Absolute Eosinophil Count", desc: "Quantifies eosinophils — elevated in allergy, asthma and parasitic infections." },
      { name: "APTT", desc: "Activated Partial Thromboplastin Time — evaluates the intrinsic clotting pathway and heparin therapy." },
    ],
  },
  {
    id: "clinical-pathology",
    number: "04",
    title: "Clinical Pathology",
    tagline: "Fluids and specimens under precise scrutiny.",
    tests: [
      { name: "Urine Routine", desc: "Physical, chemical and microscopic examination of urine — screens kidney, urinary and metabolic disorders." },
      { name: "Bence Jones Proteins", desc: "Detects abnormal light chains in urine — a key finding in multiple myeloma." },
      { name: "Stool Routine", desc: "Examines stool for parasites, ova, blood and inflammation — the first step in GI complaints." },
      { name: "Stool Occult Blood", desc: "Detects invisible blood in stool — an important screen for GI bleeding and colon cancer." },
      { name: "Semen Analysis", desc: "Evaluates count, motility and morphology of sperm — foundational for infertility assessment." },
      { name: "Sputum Gram Staining", desc: "Rapid identification of bacteria in sputum, guiding empirical treatment of pneumonia." },
      { name: "Sputum KOH", desc: "Detects fungal elements in sputum — important in immunocompromised patients." },
      { name: "Sputum for Acid-Fast Bacilli", desc: "Screens for tuberculosis by identifying acid-fast bacilli in sputum smears." },
      { name: "Mantoux Test (TT)", desc: "Tuberculin skin test to detect exposure to tuberculosis bacteria." },
      { name: "CSF Examination", desc: "Analyzes cerebrospinal fluid — vital for diagnosing meningitis and neurological disease." },
      { name: "Fluid Examination (Pleural, Ascitic, Synovial)", desc: "Cell counts, biochemistry and microbiology on body fluids to classify effusions and joint disease." },
    ],
  },
  {
    id: "hormones",
    number: "05",
    title: "Hormones",
    tagline: "The chemistry of balance.",
    tests: [
      { name: "T3, T4, TSH", desc: "The core thyroid profile — screens hypo- and hyperthyroidism." },
      { name: "FT3, FT4", desc: "Free (bioactive) thyroid hormones — a more accurate view of thyroid status when binding proteins vary." },
      { name: "FSH, LH, Prolactin", desc: "Reproductive hormones — evaluated in infertility, irregular cycles and pituitary disorders." },
      { name: "Estrogen, Progesterone", desc: "Female sex hormones — assessed in fertility workups, menopause and hormonal therapy." },
      { name: "Vitamin D", desc: "Measures 25-OH Vitamin D — deficiency contributes to bone pain, fatigue and immune issues." },
      { name: "Vitamin B12", desc: "Deficiency causes anemia and nerve damage — increasingly common in vegetarian populations." },
      { name: "Anti-Müllerian Hormone (AMH)", desc: "Estimates ovarian reserve — used in fertility planning and IVF cycles." },
      { name: "Anti-CCP", desc: "Highly specific antibody for early rheumatoid arthritis diagnosis." },
    ],
  },
  {
    id: "aids",
    number: "06",
    title: "Test for AIDS",
    tagline: "Confidential HIV screening & monitoring.",
    tests: [
      { name: "HIV Spot Test", desc: "Rapid HIV screening — results in minutes with confidential counselling." },
      { name: "HIV ELISA", desc: "Highly sensitive laboratory confirmation of HIV antibodies." },
      { name: "Viral Load", desc: "Quantifies HIV RNA in blood — monitors ART response and disease progression." },
      { name: "CD3, CD4, CD8", desc: "T-cell subset counts — track immune status in HIV and other immunodeficiencies." },
    ],
  },
  {
    id: "biochemistry",
    number: "07",
    title: "Biochemistry",
    tagline: "Metabolic clarity, one molecule at a time.",
    tests: [
      { name: "Blood Sugar F / PP / R", desc: "Fasting, post-prandial and random glucose — diagnoses and monitors diabetes." },
      { name: "Glucose Tolerance Test", desc: "Measures how efficiently the body clears a glucose load — used in gestational and borderline diabetes." },
      { name: "Glycated Hemoglobin (HbA1C)", desc: "Reflects average blood sugar over the past 3 months — the standard for diabetes control." },
    ],
  },
  {
    id: "renal",
    number: "08",
    title: "Renal Function Test",
    tagline: "Reading the kidneys' vital signs.",
    tests: [
      { name: "Blood Urea", desc: "Measures urea nitrogen — an early clue to impaired kidney function or dehydration." },
      { name: "Serum Creatinine", desc: "A more accurate marker of kidney filtration used to estimate GFR." },
      { name: "Uric Acid", desc: "Elevated levels are linked to gout, kidney stones and metabolic syndrome." },
    ],
  },
  {
    id: "liver",
    number: "09",
    title: "Liver Function Test",
    tagline: "Enzymes, proteins and pigments of the liver.",
    tests: [
      { name: "Serum Protein", desc: "Total protein & A:G ratio — reflects liver synthesis and chronic disease." },
      { name: "SGOT (AST)", desc: "Elevated in liver, cardiac and muscle injury." },
      { name: "SGPT (ALT)", desc: "The most liver-specific enzyme — rises early in hepatitis and fatty liver." },
      { name: "Serum Bilirubin", desc: "Assesses jaundice and bile flow — total, direct and indirect fractions." },
      { name: "Alkaline Phosphatase", desc: "Rises in cholestasis, bone disease and pregnancy." },
      { name: "Serum Albumin", desc: "Chronically low levels indicate liver disease, malnutrition or nephrotic syndrome." },
      { name: "Serum Phosphorous", desc: "Balances with calcium — deranged in kidney and parathyroid disorders." },
      { name: "Serum Acid Phosphatase", desc: "Historic marker used in prostate and bone assessments." },
      { name: "Serum Calcium", desc: "Vital for bones, nerves and muscles — abnormalities affect the heart rhythm." },
      { name: "Serum Magnesium", desc: "Low magnesium causes cramps, arrhythmias and refractory hypokalemia." },
      { name: "Serum Ferritin", desc: "Body's iron store — low in iron-deficiency anemia, elevated in inflammation." },
      { name: "Serum LDH", desc: "A general marker of tissue injury — used in hemolysis, tumors and infarction." },
      { name: "Gamma Glutamyl Transferase (GGT)", desc: "Sensitive to alcohol use and bile duct disease." },
    ],
  },
  {
    id: "lipid",
    number: "10",
    title: "Lipid Profile",
    tagline: "Cardiovascular risk in numbers.",
    tests: [
      { name: "Serum Cholesterol", desc: "Total cholesterol — a foundational marker of cardiovascular risk." },
      { name: "HDL", desc: "The 'good' cholesterol — higher values protect the heart." },
      { name: "Serum Triglycerides", desc: "Blood fats — high levels increase risk of pancreatitis and heart disease." },
    ],
  },
  {
    id: "histology",
    number: "11",
    title: "Histology & Cytology",
    tagline: "Tissue-level truth.",
    tests: [
      { name: "Specimen (Histopathology & Cytology)", desc: "Microscopic examination of biopsy tissue and cellular samples to detect cancer, inflammation and structural disease." },
    ],
  },
  {
    id: "other",
    number: "12",
    title: "Other Tests",
    tagline: "Cardiac markers & electrolytes.",
    tests: [
      { name: "Serum Electrolytes", desc: "Sodium, potassium and chloride — vital in hydration, cardiac and neurological conditions." },
      { name: "CPK — Total", desc: "Creatine phosphokinase — a general marker of muscle injury." },
      { name: "CPK-MB", desc: "Cardiac-specific fraction of CPK — rises in myocardial injury." },
      { name: "Troponin I", desc: "Highly specific marker of heart muscle damage." },
      { name: "Troponin T", desc: "Complementary cardiac marker used in acute coronary syndrome workup." },
    ],
  },
];
